/**
 * T11/T12 — o modelo da **regra de Human Access**.
 *
 * A TELA DIZ «REGRA», O CÓDIGO DIZ `policy` — E ISSO É DE PROPÓSITO (03/09)
 *
 *   O item dentro de Human Access chama-se **regra** em toda a interface:
 *   títulos, colunas, botões, diálogos, toasts e mensagens de validação. Por
 *   baixo, nada mudou de nome — rota, contrato, esquema, tipos, chaves de i18n e
 *   códigos de campo continuam sendo `policy`/`politica`. Renomear o
 *   identificador junto com o rótulo custaria uma migração de contrato e uma
 *   rodada de bump em quem consome, para trocar uma palavra que o usuário nunca
 *   lê.
 *
 *   Consequência prática para quem edita este arquivo: string que a tela mostra
 *   fala «regra»; nome de símbolo, chave e comentário sobre o dado falam
 *   «política». As duas palavras convivendo aqui não são inconsistência.
 */
import { t, tn } from '@app/i18n';
import type { ApiData, ApiQuery } from '@app/api/typed';
import type { components } from '@app/api/types';

export type Policy = components['schemas']['Policy'];
export type PolicyInput = components['schemas']['PolicyInput'];
export type PolicyGrant = components['schemas']['PolicyGrant'];
export type PolicyConditions = NonNullable<components['schemas']['PolicyConditions']>;
export type PolicySummary = components['schemas']['PolicySummary'];
export type A2ARef = components['schemas']['A2ARef'];
export type Identity = components['schemas']['Identity'];
export type Resource = components['schemas']['Resource'];
export type Tool = components['schemas']['Tool'];
export type RegoValidateResult = ApiData<'/api/v1/policies/validate-rego', 'post'>;

export type PoliciesQuery = NonNullable<ApiQuery<'/api/v1/policies', 'get'>>;
export type PolicyState = Policy['state'];
export type PolicyMode = Policy['mode'];
export type ClaimCondition = components['schemas']['PolicyClaimCondition'];
export type ClaimOperator = ClaimCondition['operator'];

/** Ver `UsersPagina`: a interseção com `Paginacao` derruba o tipo do item. */
export type PoliciesPage = Omit<ApiData<'/api/v1/policies'>, 'items'> & { items: Policy[] };
export type IdentitiesPage = Omit<ApiData<'/api/v1/identities'>, 'items'> & {
  items: Identity[];
};
export type ResourcesPage = Omit<ApiData<'/api/v1/resources'>, 'items'> & { items: Resource[] };

// --------------------------------------------------- condições por claim (D-7c)

/** Os quatro operadores, na ordem em que a tela os oferece. */
export const CLAIM_OPERATORS: readonly ClaimOperator[] = ['equals', 'in', 'not_in', 'exists'];

/**
 * O que cada operador quer dizer para quem escreve a política — o combobox mostra
 * isto, não o nome cru: «diferente» (`not_in`) sozinho não diz que a claim precisa existir.
 */
export function operatorHelp(operador: ClaimOperator): string {
  return t(`humanAccess.operator.${operador}`);
}

/**
 * A ETIQUETA do operador no combobox (marco 4, qa-i18n). O enum do contrato é em
 * inglês desde 11/10 (`equals · in · not_in · exists`, aim-rhf; antes, `igual · em · diferente ·
 * existe`), e a tela nunca o mostra cru: em pt-BR a etiqueta é «igual · em · diferente · existe»,
 * em `en` é a palavra da frase do PDP («equals · in · not in · exists»). O valor continua sendo o
 * enum (é o que vai no corpo do `PUT`); só a etiqueta traduz. `conditionsText` («amr em [otp, mfa]») NÃO
 * passa por aqui de propósito: espelha o texto que o backend escreve nas arestas de T19.
 */
export function operatorLabel(operador: ClaimOperator): string {
  return t(`humanAccess.operator.${operador}.label`);
}

/**
 * A etiqueta de um operador que chega como TEXTO, fora do formulário: a condição que barrou a regra no diagnóstico de
 * uma decisão, em Detalhe da decisão (T17) e no Simulador (T18) (aim-zqo). O enum do contrato traduz pela mesma chave
 * curta de `operatorLabel`, e o valor do contrato não muda. Um operador fora do enum (de um executor mais novo) sai
 * como veio, e o operador ausente sai `fallback`. É o caso de uma decisão gravada antes de 11/10, que guarda o
 * operador em português (`igual`, `em`…): o histórico não se reescreve (decisão 4 do aim-rhf).
 */
export function operatorText(operador: string | null | undefined, fallback: string): string {
  const op = operador?.trim();
  if (!op) return fallback;
  return (CLAIM_OPERATORS as readonly string[]).includes(op) ? operatorLabel(op as ClaimOperator) : op;
}

/**
 * Sugestões do combobox de claim — **sugestão, não vocabulário**. O campo aceita
 * texto livre, que é o ponto inteiro de D-7c: claim nova não pode exigir deploy.
 *
 * As três primeiras são as que o STS/PDP do lab entende pelo nome (o Rego base
 * compara `object.get(token, <claim>, null)`, sem tradução em lugar nenhum); as
 * duas últimas são as que o token do IdP emulado já traz.
 */
export const CLAIMS_SUGGESTED: readonly string[] = [
  'subject_type',
  'amr',
  'channel',
  'perfil',
  'sub',
];

/**
 * Os values que cada claim conhecida costuma trazer — **sugestão de valor**, o
 * par da sugestão de claim.
 *
 * Até 28/08 este vocabulário era um trio de controles fixos na aba Condições
 * (checkbox de Tipo de Human Subject, checkbox de Canal, combobox de amr). Ele é
 * da Vivo, não do produto: um cliente cujo STS emita `pf`/`pj` em `subject_type`
 * via três campos que não servem para nada e nenhum lugar para escrever o que
 * serve. Como sugestão, ele continua poupando digitação de quem está no roteiro
 * e some do caminho de quem não está.
 *
 * Chave ausente = claim sem vocabulário conhecido; o campo de valor segue livre.
 */
export const VALUES_SUGERIDOS: Readonly<Record<string, readonly string[]>> = {
  subject_type: ['customer', 'employee', 'prospect'],
  channel: ['ura-voz', 'sso'],
  amr: ['otp', 'mfa'],
};

/** Os values sugeridos da claim, ou lista vazia — nunca `undefined`. */
export function valuesSugeridos(claim: string): readonly string[] {
  return VALUES_SUGERIDOS[claim.trim()] ?? [];
}

/** `exists` é o único operador que não leva values — e recusa se levar. */
export function operatorRequiresValues(operador: ClaimOperator): boolean {
  return operador !== 'exists';
}

/**
 * A condição em linguagem de produto: «amr em [otp, mfa]», «caller_type igual
 * employee», «tenant_id existe» (em `en`: «amr in [otp, mfa]», «tenant_id exists»).
 *
 * **Espelha `accessmap.TextoDaCondicaoPorClaim` do backend**, que escreve o
 * rótulo das arestas de T19 no servidor. As duas leituras têm de bater: a mesma
 * política aparecendo com dois textos em duas telas é o tipo de divergência que
 * ninguém nota até alguém decidir com base na errada.
 */
export function claimTextCondition(condicao: ClaimCondition): string {
  // A palavra do operador é a ETIQUETA do catálogo (Lote 3), nos dois idiomas. Desde 11/10 o
  // enum é em inglês (aim-rhf), e a etiqueta pt-BR deixou de ser o próprio enum: o rótulo que o
  // servidor ainda escreve nas arestas do Mapa de acesso (T19) passa a sair do catálogo na tela,
  // pelo mesmo caminho daqui (parte 6 do aim-rhf).
  const operador = operatorLabel(condicao.operator);
  if (condicao.operator === 'exists' || condicao.values.length === 0) {
    return `${condicao.claim} ${operador}`;
  }
  if (condicao.operator === 'equals') {
    return `${condicao.claim} ${operador} ${condicao.values[0]}`;
  }
  return `${condicao.claim} ${operador} [${condicao.values.join(', ')}]`;
}

/**
 * O que impede salvar as condições por claim, com a mesma regra do backend
 * (`policies.claimsValidas`) e do middleware (`materializar.condicoes_invalidas`).
 *
 * Validar aqui não é duplicação: o servidor recusa com 422 e o campo, mas cobrar
 * antes evita a ida ao servidor para dizer o que a tela já sabe — e o índice
 * aponta **qual linha** do editor corrigir.
 */
export interface ClaimProblem {
  indice: number;
  message: string;
}

export function claimsProblem(claims: ClaimCondition[]): ClaimProblem | null {
  for (let i = 0; i < claims.length; i += 1) {
    const c = claims[i];
    if (!c.claim.trim()) {
      return { indice: i, message: t('humanAccess.validation.claimRequired') };
    }
    if (!operatorRequiresValues(c.operator)) {
      if (c.values.length) {
        return { indice: i, message: t('field.existsTakesNoValues') };
      }
      continue;
    }
    if (c.values.length === 0) {
      return {
        indice: i,
        // A etiqueta do operador, e não o enum do contrato, que é português (aim-zqo).
        message: t('humanAccess.validation.operatorNeedsValue', { operator: operatorLabel(c.operator) }),
      };
    }
    if (c.operator === 'equals' && c.values.length > 1) {
      return { indice: i, message: t('field.equalsTakesOneValue') };
    }
    if (c.values.some((v) => !v.trim())) {
      return { indice: i, message: t('humanAccess.validation.emptyValue') };
    }
  }
  return null;
}

// ------------------------------------------------------- P6.2: os dois eixos

/**
 * De quais eixos a regra fala. O valor vem do servidor (`Policy.axis`) e não é
 * recalculado aqui.
 *
 * O BADGE «EIXO» SAIU DA LISTA EM 03/09, E OS RÓTULOS FORAM COM ELE
 *
 *   A guarda de visibilidade da P6.2 desenhava este valor como badge em T11.
 *   Com a reorganização Human × Identity Access a coluna morreu: em Human Access
 *   **tudo** é eixo pessoa, e o badge passaria a repetir a mesma palavra em toda
 *   linha. O campo continua no contrato — o hub consolidado da Fase 2 é quem
 *   volta a lê-lo, do lado de fora da lista.
 */
export type PolicyAxis = Policy['axis'];

/**
 * O alcance de «todas as identidades», em linguagem de produto — a frase que o
 * formulário mostra antes de confirmar (P6.2 › guarda de visibilidade 1).
 *
 * O número é o de identidades **atribuídas** (com slug): identidade sem slug não
 * tem o que publicar no PDP, então contá-la prometeria alcance que não existe.
 */
export function reachText(assigned: number): string {
  return t('humanAccess.reach.suffix', {
    today: tn('humanAccess.reach.today', assigned),
  });
}

/**
 * O contador das grades de multi-seleção do drawer: «5 de 23 selecionadas»,
 * e «· mostrando 3» quando um filtro está aplicado.
 *
 * As duas grades (Identities e Capabilities) dizem a mesma coisa com as mesmas
 * palavras de propósito: a seleção em massa age só no que o filtro mostra, e o
 * número que separa «selecionadas» de «mostrando» é o que deixa isso visível.
 * `mostrando = null` significa «sem filtro» — repetir o total ali seria ruído.
 */
export function selectionText(selected: number, total: number, mostrando: number | null): string {
  const base = t('humanAccess.selection.count', { selected, total });
  return mostrando === null
    ? base
    : t('humanAccess.selection.showing', { base, showing: mostrando });
}

/** Rótulo do estado na lista e no drawer: `ativa · v{N}` / `rascunho · v{N}`. */
/**
 * `ativa · v3`. O ESTADO é enum do contrato (`ativa`/`rascunho`) e vira rótulo
 * pelo catálogo — o valor cru continua sendo o do servidor, e é ele que filtra.
 */
export function stateLabel(politica: Policy): string {
  return t('humanAccess.state.label', {
    state: t(politica.state === 'ativa' ? 'humanAccess.state.active' : 'humanAccess.state.draft'),
    version: politica.version,
  });
}

/** Rótulo do modo (§4.6 do inventário) — o do drawer, que tem largura de sobra. */
export function modeLabel(modo: PolicyMode): string {
  return t(modo === 'rego' ? 'humanAccess.mode.rego' : 'humanAccess.mode.interface');
}

/**
 * O mesmo modo, na largura da lista: `interface` · `rego`.
 *
 * A lista tem sete colunas em ~1100px, e «interface · Rego gerado» pedia 203px
 * sozinho — o bastante para empurrar «Estado» e «Ações» para fora da tela. O
 * wireframe da T11 refeita já escreve o modo assim (spec «Acesso: pessoa ×
 * agente» §4); a forma longa continua no drawer, onde ela explica o que a forma
 * curta só nomeia.
 */
export function modeShortLabel(modo: PolicyMode): string {
  return t(modo === 'rego' ? 'humanAccess.mode.short.rego' : 'humanAccess.mode.short.interface');
}

/**
 * As condições de uma política como **uma lista só**, na forma canônica — a mesma
 * que vai ao PDP.
 *
 * O AÇÚCAR DEPRECADO ENTRA CONVERTIDO, E NÃO IGNORADO
 *
 *   `subject_types`/`amr`/`channels` saíram da tela em 28/08 (eram o vocabulário
 *   da Vivo cravado no produto) e o front não os emite mais. Mas o contrato os
 *   aceita, e as políticas gravadas antes ainda os têm — e o backend continua
 *   publicando-as **com** essa restrição. Ignorá-los aqui faria a tela dizer «sem
 *   condições» sobre uma política que restringe no PDP, que é a divergência mais
 *   cara possível nesta tela.
 *
 * A ordem espelha `domain.PoliticaCondicao.Canonicas` do backend: açúcar primeiro
 * (`subject_type`, `amr`, `channel`), depois as claims na ordem escrita.
 */
export function conditionClaims(condicoes?: PolicyConditions | null): ClaimCondition[] {
  if (!condicoes) return [];
  const fora: ClaimCondition[] = [];
  if (condicoes.subject_types?.length) {
    fora.push({ claim: 'subject_type', operator: 'in', values: [...condicoes.subject_types] });
  }
  if (condicoes.amr) fora.push({ claim: 'amr', operator: 'in', values: [condicoes.amr] });
  if (condicoes.channels?.length) {
    fora.push({ claim: 'channel', operator: 'in', values: [...condicoes.channels] });
  }
  // Cópia rasa por item: o editor muda `valores` no lugar, e reusar o array da
  // resposta faria a lista da tela e o cache do react-query apontarem o mesmo
  // objeto — o "cancelar" do drawer deixaria de desfazer.
  for (const claim of condicoes.claims ?? []) fora.push({ ...claim, values: [...claim.values] });
  return fora;
}

/**
 * `condText` do protótipo, agora só com as condições por claim em linguagem de
 * produto: «subject_type em [employee] · amr em [otp]» · `sem condições`.
 */
export function conditionsText(condicoes?: PolicyConditions | null): string {
  const parts = conditionClaims(condicoes).map(claimTextCondition);
  return parts.length ? parts.join(' · ') : t('humanAccess.conditions.none');
}

/**
 * Quantas condições a coluna «Quem pede» de T11 mostra antes do chip «+n»
 * (spec «Acesso: pessoa × agente» §4, caso-limite «muitas claims»).
 *
 * A coluna nunca quebra em duas linhas: a lista é para varrer, não para ler
 * regra inteira — a verdade completa mora no drawer. O chip esconde do olho, não
 * da busca: o filtro da lista é do servidor e continua procurando dentro das
 * condições escondidas e no fonte Rego.
 */
export const CLAIMS_VISIBLE_IN_LIST = 2;

export interface ClaimsOverflow {
  /** As condições que a coluna desenha. */
  visible: string[];
  /** As que ficam atrás do chip «+n» — o hover as lista. */
  hidden: string[];
}

/** As condições da política partidas em «as que aparecem» e «as do +n». */
export function claimsOverflow(condicoes?: PolicyConditions | null): ClaimsOverflow {
  const todas = conditionClaims(condicoes).map(claimTextCondition);
  return {
    visible: todas.slice(0, CLAIMS_VISIBLE_IN_LIST),
    hidden: todas.slice(CLAIMS_VISIBLE_IN_LIST),
  };
}

/**
 * "3 capabilities" / "nada ainda" / "definido no Rego manual".
 *
 * Sem a parte «· 1 A2A» desde DEF-32: a regra não concede A2A (o `a2a` da
 * resposta é sempre `[]` e está deprecated) — a delegação é do contrato da
 * identidade, em Identity Access.
 */
export function grantsText(politica: Pick<Policy, 'mode' | 'grants'>): string {
  if (politica.mode === 'rego') return t('humanAccess.grants.rego');
  return politica.grants.length
    ? t('humanAccess.grants.count', { n: politica.grants.length })
    : t('humanAccess.grants.none');
}

// --------------------------------------------------------------------- Rego

/**
 * Pacote que o módulo precisa declarar: `aim.policy.<slug>`.
 *
 * O middleware grava o módulo em `aim/policy/{slug}` e o Rego base agrega
 * `data.aim.policy.*` — mas a declaração `package` viaja **dentro do texto** que
 * a pessoa escreve. Pacote errado grava com `200`, aparece ativa e o `deny` cai
 * numa subárvore que ninguém consulta: nega nada, e o usuário acredita que
 * restringiu (`middleware-ordens.md › 8a`). Por isso a tela avisa antes.
 */
export function packageExpected(slug?: string | null): string | null {
  return slug ? `aim.policy.${slug.replace(/-/g, '_')}` : null;
}

/** `package X` declarado no texto, ou `null` quando não há declaração. */
export function packageDeclared(rego: string): string | null {
  return /^\s*package\s+([^\s]+)/m.exec(rego)?.[1] ?? null;
}

export type PackageWarning = 'ausente' | 'divergente' | null;

/** `null` = o pacote confere (ou ainda não há slug para comparar). */
export function checksPackage(rego: string, slug?: string | null): PackageWarning {
  if (!rego.trim()) return null;
  const declarado = packageDeclared(rego);
  if (!declarado) return 'ausente';
  const expected = packageExpected(slug);
  if (!expected) return null;
  return declarado === expected ? null : 'divergente';
}

// ------------------------------------------------------------------ filtros

export interface PoliciesFilters {
  q: string;
  state: PolicyState | null;
  mode: PolicyMode | null;
  page: number;
}

export const FILTERS_INICIAIS: PoliciesFilters = { q: '', state: null, mode: null, page: 1 };

export function hasFilterApplied(filters: PoliciesFilters): boolean {
  return Boolean(filters.q.trim() || filters.state || filters.mode);
}

export function forQuery(filters: PoliciesFilters): PoliciesQuery {
  return {
    ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
    ...(filters.state ? { state: filters.state } : {}),
    ...(filters.mode ? { mode: filters.mode } : {}),
    page: filters.page,
    sort: 'name',
  };
}

// ----------------------------------------------------------------- o drawer

export interface PolicyFormValues {
  name: string;
  description: string;
  mode: PolicyMode;
  /** O toggle **publicada no PDP** da aba Geral. */
  ativa: boolean;
  /**
   * O atalho «todas» (P6.2): a política vale como se cada identidade aprovada
   * estivesse listada, **e as futuras herdam**. Exclusivo com `identities` — o
   * bloco «Por qual agente» é ou um ou outro.
   */
  allIdentities: boolean;
  identities: string[];
  grants: PolicyGrant[];
  /**
   * As condições da política (D-7c) — a única forma que a tela escreve desde
   * 28/08. Uma política antiga chega com o açúcar deprecado e abre aqui já
   * convertida (`claimsDaCondicao`), de modo que salvar não apague a restrição.
   */
  claims: ClaimCondition[];
  rego_text: string;
}

/**
 * Exemplo de módulo que a textarea mostra vazia. Vai ao catálogo (e é função,
 * não const) porque o nome do pacote é do domínio de quem escreve: um
 * `minha_politica` numa tela em inglês ensina um slug em português — a mesma
 * razão do exemplo de claims do Simulador.
 */
export function regoPlaceholder(): string {
  return t('humanAccess.rego.placeholder');
}

export function policyDefaults(politica?: Policy): PolicyFormValues {
  return {
    name: politica?.name ?? '',
    description: politica?.description ?? '',
    mode: politica?.mode ?? 'interface',
    ativa: politica?.state === 'ativa',
    allIdentities: politica?.all_identities ?? false,
    identities: politica?.identities.map((i) => i.identity_id) ?? [],
    grants: politica?.grants ?? [],
    claims: conditionClaims(politica?.conditions),
    rego_text: politica?.rego_text ?? '',
  };
}

/**
 * Condições só vão ao servidor quando existe alguma — `null` = sem condições.
 *
 * Sai **só** `claims`: o açúcar (`subject_types`/`amr`/`channels`) está deprecado
 * no contrato e a PoC deixou de o emitir em 28/08. Uma política antiga que o
 * tenha no banco chega aqui já convertida por `claimsDaCondicao`, então salvar
 * reescreve a mesma restrição na forma canônica — não a apaga.
 */
function forConditions(values: PolicyFormValues): PolicyConditions | null {
  // `[]` e ausente significam o mesmo, e o contrato prefere a ausência (regra 8
  // do CLAUDE.md) — mas sem nenhuma condição a chave inteira é `null`.
  if (!values.claims.length) return null;
  return {
    claims: values.claims.map((c) => ({
      claim: c.claim.trim(),
      operator: c.operator,
      // `exists` não leva values — mandá-los é 422 no servidor e no middleware.
      // Descartar aqui é o que deixa trocar o operador na tela sem ter de limpar
      // os chips à mão.
      values: operatorRequiresValues(c.operator) ? c.values.map((v) => v.trim()) : [],
    })),
  };
}

export function forInput(values: PolicyFormValues, politica?: Policy): PolicyInput {
  const rego = values.mode === 'rego';
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    mode: values.mode,
    state: values.ativa ? 'ativa' : 'rascunho',
    // Em Rego manual quem concede é a regra: nada de identidade/capability juntas.
    // O atalho «todas» é da interface pelo mesmo motivo: em Rego a cobertura é o
    // que o módulo decidir, e mandá-lo ligado publicaria `all_applications` numa
    // política que nem entra no documento §7.
    all_identities: rego ? false : values.allIdentities,
    identities: rego || values.allIdentities ? [] : values.identities,
    grants: rego ? [] : values.grants,
    // Sem `a2a` (DEF-32): a regra não concede A2A. O campo saiu do `PolicyInput`
    // em 08/09 — mandá-lo hoje é chave desconhecida (`400 requisicao_invalida`).
    conditions: rego ? null : forConditions(values),
    ...(rego ? { rego_text: values.rego_text } : {}),
    // Trava otimista na edição (409 `versao_antiga`).
    ...(politica ? { version: politica.version } : {}),
  };
}

/**
 * O que impede salvar, na ordem em que a tela cobra (T12, FR-081, FR-082).
 * `null` = pode salvar. Cada item aponta a aba que precisa de atenção.
 */
export interface Impedimento {
  aba: 'geral' | 'identities' | 'conditions' | 'rego';
  message: string;
}

/**
 * As duas regras do atalho «todas» (P6.2). Valem **também no rascunho**, como no
 * servidor: o rascunho é o que a pessoa volta para ativar depois, e deixá-lo
 * torto adiaria a recusa para o momento em que ela já acha que terminou.
 */
export function allIdentitiesImpediment(values: PolicyFormValues): Impedimento | null {
  if (!values.allIdentities) return null;
  if (values.identities.length) {
    return {
      aba: 'identities',
      message: t('field.allIdentitiesTakesNoList'),
    };
  }
  if (!values.claims.length) {
    return {
      aba: 'conditions',
      message: t('humanAccess.block.allIdentitiesNeedsCondition'),
    };
  }
  return null;
}

/**
 * TODA REGRA ATIVA EXIGE AO MENOS UMA CONDIÇÃO EM «QUEM PEDE» (03/09)
 *
 *   Não é mais uma regra do atalho «todas»: vale igual para a lista nomeada. No
 *   modelo Human × Identity Access o eixo workload é **só o contrato da
 *   identidade**, e o que a regra de Human Access acrescenta é o eixo pessoa. Uma
 *   regra sem claim não diz nada sobre quem pede, então não entra na interseção:
 *   ela é **inerte**. Publicá-la ativa mostraria «ativa · vN» na lista sobre uma
 *   regra que não libera ninguém — e essa é a mentira mais cara que esta tela
 *   pode contar.
 *
 *   Vale só para **ativa**: o rascunho é o meio do preenchimento, e cobrar a
 *   claim antes de a pessoa chegar na aba seria cobrar o que ela ainda vai
 *   escrever. A exceção é «todas», que a P6.2 cobra desde o rascunho por outro
 *   motivo (alcance com herança), e continua como está.
 *
 *   O servidor recusa com o mesmo código de campo da P6.2
 *   (`allIdentitiesNeedsClaim`) — um código, as duas guardas. O texto do catálogo
 *   (`i18n/locales/pt-BR/field.json`) foi generalizado junto.
 */
export function activeNeedsClaimImpediment(values: PolicyFormValues): Impedimento | null {
  if (!values.ativa || values.mode !== 'interface' || values.claims.length) return null;
  // A mesma frase do catálogo (`field.allIdentitiesNeedsClaim`), de propósito: o
  // front recusa antes de chamar e o servidor recusa depois, e as duas portas
  // dizem a mesma coisa. O porquê inteiro — a interseção — mora no aviso da aba
  // «Quem pede», onde há espaço para explicá-lo.
  //
  // SEM O «ATIVA» NA FRASE (04/09): o mesmo código sai para «todas as
  // identidades» sem claim **em rascunho**, e ali «regra ativa exige» descreveria
  // um caso que não é o do usuário. «Esta regra precisa» vale nos dois.
  return {
    aba: 'conditions',
    message: t('humanAccess.block.needsCondition'),
  };
}

export interface ImpedimentoContext {
  /** Validação do Rego pelo executor: `false` = "aguardando Rego base" (FR-082). */
  validacaoDisponivel: boolean;
  /** O texto atual já foi validado com sucesso. */
  regoValidado: boolean;
}

export function saveImpedimento(
  values: PolicyFormValues,
  { validacaoDisponivel, regoValidado }: ImpedimentoContext,
): Impedimento | null {
  if (!values.name.trim()) {
    return { aba: 'geral', message: t('humanAccess.block.nameRequired') };
  }
  if (values.mode === 'rego' && !values.rego_text.trim()) {
    return { aba: 'rego', message: t('humanAccess.block.regoRequired') };
  }
  // Condição malformada é impedimento **de rascunho também**: o rascunho é o que
  // a pessoa volta para ativar depois, e gravá-lo torto adiaria a recusa para o
  // momento em que ela já acha que terminou (o servidor recusa nos dois casos).
  if (values.mode === 'interface') {
    const problem = claimsProblem(values.claims);
    if (problem) {
      return {
        aba: 'conditions',
        message: t('humanAccess.block.conditionPrefix', {
          n: problem.indice + 1,
          message: problem.message,
        }),
      };
    }
    const todas = allIdentitiesImpediment(values);
    if (todas) return todas;
  }
  if (!values.ativa) return null;

  // A ordem das duas guardas de «ativa» é a ordem das abas: «Quem pede» antes de
  // «Identities». A regra se lê da esquerda para a direita — quem pede, depois o
  // que ela alcança — e mandar a pessoa para a primeira aba incompleta é o que o
  // resto do drawer já faz.
  const missingClaim = activeNeedsClaimImpediment(values);
  if (missingClaim) return missingClaim;

  // A FR-080 generalizada pela P6.2: ativa precisa cobrir alguém do lado do
  // workload — a lista OU o atalho. Nos dois eixos da fórmula a política só entra
  // na conta se cobre o agente, então ativa sem cobertura é política publicada
  // que decisão nenhuma alcança.
  if (values.mode === 'interface' && !values.allIdentities && values.identities.length === 0) {
    return {
      aba: 'identities',
      message: t('humanAccess.block.identitiesRequired'),
    };
  }
  if (values.mode === 'rego' && !validacaoDisponivel) {
    return {
      aba: 'rego',
      message: t('humanAccess.block.regoValidationUnavailable'),
    };
  }
  if (values.mode === 'rego' && !regoValidado) {
    return { aba: 'rego', message: t('humanAccess.block.validateRegoFirst') };
  }
  return null;
}
